import {
  Directive,
  ElementRef,
  HostListener,
  Injectable,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  inject,
} from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';

/**
 * Native-Angular port of the AngularJS `flipto.components.states` module
 * (account-v1/.../components/states/states.js, 225 LOC).
 *
 * A simple global state machine — states have a name + optional group;
 * activating a state deactivates its siblings (same group); directives
 * react to state changes by adding/removing CSS classes. Used by the
 * journeys page to drive `<div ft-state-class="{ 'active': 'guest-journeys.prestay' }">`
 * panels that toggle between the "set up your variations" empty state
 * and the active variations list.
 *
 * REMOVE when the hybrid bridge is cut: replace with proper @ngrx/store
 * slices at the Angular-21 milestone.
 */
export interface FtState {
  name: string;
  group?: string;
}

@Injectable({ providedIn: 'root' })
export class FtStatesService {
  private readonly states: FtState[] = [];
  private readonly activeStates$ = new BehaviorSubject<FtState[]>([]);

  /** Stream of the current active states — directives subscribe to react. */
  get changes() { return this.activeStates$.asObservable(); }

  addState(state: FtState): void {
    if (!state?.name) return;
    if (!this.getState(state.name)) this.states.push(state);
  }

  removeState(stateName: string): void {
    const idx = this.states.findIndex((s) => s.name === stateName);
    if (idx >= 0) this.states.splice(idx, 1);
    const next = this.activeStates$.value.filter((s) => s.name !== stateName);
    if (next.length !== this.activeStates$.value.length) this.activeStates$.next(next);
  }

  activate(stateName: string): void {
    const state = this.getState(stateName);
    if (!state) return;
    const siblings = this.getSiblings(state);
    const current = this.activeStates$.value;
    const next = current.filter(
      (active) => !(active.name !== state.name && siblings.some((s) => s.name === active.name)),
    );
    if (!next.some((s) => s.name === state.name)) next.push(state);
    this.activeStates$.next(next);
  }

  isActive(stateName: string): boolean {
    return this.activeStates$.value.some((s) => s.name === stateName);
  }

  private getState(stateName: string): FtState | undefined {
    return this.states.find((s) => s.name === stateName);
  }

  private getSiblings(state: FtState): FtState[] {
    return this.states.filter((s) => s.group === state.group);
  }
}

/**
 * `<div ft-state="guest-journeys.prestay" group="..." active>` — registers
 * a state with the service and reacts to its active/inactive transitions
 * via the state-active / state-inactive CSS classes the v1 directive
 * uses for animations.
 */
@Directive({
    selector: '[ft-state]',
    standalone: false
})
export class FtStateDirective implements OnInit, OnDestroy {
  @Input('ft-state') stateName!: string;
  @Input() group?: string;
  /** Presence-only attribute — v1: `angular.isDefined(attrs.active)`. */
  @Input() active: boolean | '' | null | undefined;

  private sub?: Subscription;

    private readonly states = inject(FtStatesService);
    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

  ngOnInit(): void {
    this.renderer.addClass(this.el.nativeElement, 'state');
    this.renderer.addClass(this.el.nativeElement, 'state-inactive');
    this.states.addState({ name: this.stateName, group: this.group });
    if (this.active === '' || this.active === true || (this.active != null && String(this.active) !== 'false')) {
      this.states.activate(this.stateName);
    }
    this.sub = this.states.changes.subscribe(() => this.refresh());
    this.refresh();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.states.removeState(this.stateName);
  }

  private refresh(): void {
    const active = this.states.isActive(this.stateName);
    if (active) {
      this.renderer.removeClass(this.el.nativeElement, 'state-inactive');
      this.renderer.addClass(this.el.nativeElement, 'state-active');
    } else {
      this.renderer.removeClass(this.el.nativeElement, 'state-active');
      this.renderer.addClass(this.el.nativeElement, 'state-inactive');
    }
  }
}

/**
 * `<a ft-set-state="guest-journeys.prestay">` — clicking activates the
 * named state in the service.
 */
@Directive({
    selector: '[ft-set-state]',
    standalone: false
})
export class FtSetStateDirective {
  @Input('ft-set-state') stateName!: string;

    private readonly states = inject(FtStatesService);

  @HostListener('click') onClick(): void {
    if (this.stateName) this.states.activate(this.stateName);
  }
}

/**
 * `<div ft-state-class="{'active': 'guest-journeys.prestay'}" [inverse]>`
 * — applies the map's keys (class names) when the corresponding state is
 * active. `inverse` attribute swaps the polarity (apply class when NOT
 * active). v1's `attrs.ftStateClass` was an inline expression evaluated
 * via `scope.$eval`; Angular bindings accept the literal map directly via
 * property binding (`[ft-state-class]="{ ... }"`) or the attribute form
 * is parsed JSON-ish at runtime here.
 *
 * The journeys template uses the attribute string form
 * `ft-state-class="{'active': 'guest-journeys.prestay'}"`. Single-quoted
 * keys/values aren't valid JSON, so the parser permits both single + double.
 */
@Directive({
    selector: '[ft-state-class]',
    standalone: false
})
export class FtStateClassDirective implements OnInit, OnDestroy {
  @Input('ft-state-class') stateClassExpr: string | Record<string, string> = {};
  /** Presence-only inverse flag — `<div ft-state-class="..." inverse>`. */
  @Input() inverse: boolean | '' | null | undefined;

  private sub?: Subscription;
  private committed: string[] = [];

    private readonly states = inject(FtStatesService);
    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

  ngOnInit(): void {
    this.sub = this.states.changes.subscribe(() => this.refresh());
    this.refresh();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private refresh(): void {
    const map = this.parseMap(this.stateClassExpr);
    const isInverse = this.inverse === '' || this.inverse === true || (this.inverse != null && String(this.inverse) !== 'false');
    const next: string[] = [];
    for (const [className, stateName] of Object.entries(map)) {
      if (this.states.isActive(stateName) === !isInverse) next.push(className);
    }
    for (const c of this.committed) this.renderer.removeClass(this.el.nativeElement, c);
    for (const c of next) this.renderer.addClass(this.el.nativeElement, c);
    this.committed = next;
  }

  /** Parse v1's loose object-literal attribute, e.g. {'active': 'guest-journeys.prestay'}. */
  private parseMap(expr: string | Record<string, string>): Record<string, string> {
    if (typeof expr === 'object' && expr !== null) return expr;
    if (typeof expr !== 'string' || !expr) return {};
    // Permissive: convert single-quoted keys/values to double-quoted, then JSON.parse.
    try {
      const normalized = expr.replace(/'/g, '"');
      return JSON.parse(normalized) ?? {};
    } catch {
      return {};
    }
  }
}
